import { useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { AutoGrid } from '@factorialco/factorial-one-react/dist/experimental'
import {
  Image,
  Megaphone,
  Microphone,
  VideoRecorder,
} from '@factorialco/factorial-one-react/icons/app'
import { LocalUserChoices } from '@livekit/components-react'

import i18n from 'modules/core/lib/i18n'

import { ControlBar } from 'modules/meetings/components/ControlBar'
import { MediaDeviceSelect } from 'modules/meetings/components/MediaDeviceSelect'
import {
  VIRTUAL_BACKGROUND_TYPES,
  getVirtualBackgroundOptions,
} from 'modules/meetings/lib/constants/VirtualBackground'
import { isVirtualBackgroundSupported } from 'modules/meetings/lib/utils/virtualBackgroundUtils'

interface PrejoinControlBarProps {
  localUserChoices: LocalUserChoices
  handleMicToggle: () => void
  handleCameraToggle: () => void
  handleAudioInputChange: (value: string) => void
  handleVideoInputChange: (value: string) => void
  handleAudioOutputChange: (value: string) => void
  handleBackgroundChange: (value: string) => void
  audioInputDevices: MediaDeviceInfo[]
  videoInputDevices: MediaDeviceInfo[]
  audioOutputDevices: MediaDeviceInfo[]
  audioOutputDeviceId: string
  backgroundType: string
}

export const PrejoinControlBar = ({
  localUserChoices,
  handleMicToggle,
  handleCameraToggle,
  handleAudioInputChange,
  handleVideoInputChange,
  handleAudioOutputChange,
  handleBackgroundChange,
  audioInputDevices,
  videoInputDevices,
  audioOutputDevices,
  audioOutputDeviceId,
  backgroundType,
}: PrejoinControlBarProps) => {
  const isAudioOutputSelectionSupported = 'setSinkId' in HTMLMediaElement.prototype
  const [backgroundSupported, setBackgroundSupported] = useState(false)

  // Check blur support on mount
  useEffect(() => {
    isVirtualBackgroundSupported().then((supported) => setBackgroundSupported(supported))
  }, [])

  return (
    <Box flexDirection="column" gap="s12" style={{ width: '100%' }} position="relative">
      <ControlBar
        handleMicToggle={handleMicToggle}
        handleCameraToggle={handleCameraToggle}
        audioEnabled={localUserChoices.audioEnabled}
        videoEnabled={localUserChoices.videoEnabled}
        labels={{
          microphone: i18n.t('meetings.prejoin.control_bar.microphone.label'),
          camera: i18n.t('meetings.prejoin.control_bar.camera.label'),
          exit: i18n.t('meetings.prejoin.control_bar.exit'),
        }}
      />
      <AutoGrid gap="2" tileSize="sm">
        <MediaDeviceSelect
          onChange={handleAudioInputChange}
          options={audioInputDevices.map((device) => ({
            label: device.label || `Microphone ${device.deviceId.substring(0, 5)}...`,
            value: device.deviceId,
          }))}
          placeholder={i18n.t('meetings.prejoin.control_bar.microphone.placeholder')}
          searchBoxPlaceholder={i18n.t(
            'meetings.prejoin.control_bar.microphone.search_placeholder'
          )}
          searchEmptyMessage={i18n.t(
            'meetings.prejoin.control_bar.microphone.no_microphones_found'
          )}
          value={localUserChoices.audioDeviceId}
          enabled
          icon={Microphone}
        />
        {isAudioOutputSelectionSupported && (
          <MediaDeviceSelect
            onChange={handleAudioOutputChange}
            options={audioOutputDevices.map((device) => ({
              label: device.label || `Speaker ${device.deviceId.substring(0, 5)}...`,
              value: device.deviceId,
            }))}
            placeholder={i18n.t('meetings.prejoin.control_bar.speakers.placeholder')}
            searchBoxPlaceholder={i18n.t(
              'meetings.prejoin.control_bar.speakers.search_placeholder'
            )}
            searchEmptyMessage={i18n.t('meetings.prejoin.control_bar.speakers.no_speakers_found')}
            value={audioOutputDeviceId}
            icon={Megaphone}
            enabled
          />
        )}
        <MediaDeviceSelect
          onChange={handleVideoInputChange}
          options={videoInputDevices.map((device) => ({
            label:
              device.label ||
              `${i18n.t('meetings.prejoin.control_bar.camera.label')} ${device.deviceId}`,
            value: device.deviceId,
          }))}
          placeholder={i18n.t('meetings.prejoin.control_bar.camera.placeholder')}
          searchBoxPlaceholder={i18n.t('meetings.prejoin.control_bar.camera.search_placeholder')}
          searchEmptyMessage={i18n.t('meetings.prejoin.control_bar.camera.no_cameras_found')}
          value={localUserChoices.videoDeviceId}
          icon={VideoRecorder}
          enabled
        />
        {backgroundSupported && (
          <MediaDeviceSelect
            onChange={(value) => handleBackgroundChange(value)}
            options={getVirtualBackgroundOptions()}
            placeholder={i18n.t('meetings.room.settings.blur_background.placeholder')}
            searchBoxPlaceholder={i18n.t(
              'meetings.room.settings.blur_background.search_placeholder'
            )}
            searchEmptyMessage={i18n.t(
              'meetings.room.settings.blur_background.no_backgrounds_found'
            )}
            enabled
            value={backgroundType || VIRTUAL_BACKGROUND_TYPES.DISABLED}
            icon={Image}
          />
        )}
      </AutoGrid>
    </Box>
  )
}
